import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { initSentry, ErrorBoundary, getLastSentryEventId } from './lib/sentry'
import { initPostHog } from './services/posthog'
import { buildSupportMailto } from './utils/supportMailto'
import './index.css'
import App from './App.jsx'

// Initialize error tracking (no-ops if VITE_SENTRY_DSN not set)
initSentry();

// Initialize product analytics (no-ops if VITE_POSTHOG_KEY not set).
// Fire-and-forget — the service awaits its own lazy-loaded SDK chunk.
initPostHog();

// Fallback UI if the React tree crashes — prevents the dreaded blank white screen.
// Sentry still captures the original error in the background.
function AppCrashFallback({ error, resetError }) {
  const eventId = getLastSentryEventId();
  const reportMailto = buildSupportMailto({
    subject: `Juuni — crash report${eventId ? ` (event ${eventId.slice(0, 8)})` : ''}`,
    sentryEventId: eventId,
    intro: `The app crashed with the following error:\n\n${error?.message || '(no message)'}\n\nI was trying to:\n(describe what you were doing when this happened)`,
  });

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: '#FDFBF7',
    }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace", color: '#C08B2A', letterSpacing: '0.08em', marginBottom: 12 }}>
          JUUNI
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 8px', color: '#1a1a1a' }}>
          Something broke on this page
        </h1>
        <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: '0 0 20px' }}>
          We've been notified and will look into it. You can try reloading, or head back to the home screen.
        </p>
        {eventId && (
          <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: '#999', marginBottom: 16 }}>
            Error ID: {eventId.slice(0, 8)}
          </div>
        )}
        {import.meta.env.DEV && error?.message && (
          <pre style={{
            textAlign: 'left', background: '#fff', border: '1px solid #eee', borderRadius: 8,
            padding: 12, fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            color: '#c00', overflow: 'auto', maxHeight: 200, marginBottom: 16,
          }}>
            {error.message}
          </pre>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => { resetError(); }}
            style={{
              padding: '10px 18px', border: '1px solid rgba(0,0,0,0.12)', background: '#fff',
              borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#1a1a1a',
            }}>
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{
              padding: '10px 18px', border: 'none', background: '#1a1a1a', color: '#fff',
              borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500,
            }}>
            Go home
          </button>
          <a href={reportMailto} style={{
            padding: '10px 18px', border: '1px solid rgba(0,0,0,0.12)', background: '#fff',
            borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#1a1a1a',
            textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
          }}>
            Report this to support
          </a>
        </div>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary fallback={AppCrashFallback} showDialog={false}>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
